import type {AccountStatus, KycStatus, User} from "@/types/user.types";
import {type ApiPagination, type NormalizedPagination, normalizePagination} from "@/utils/pagination";

interface UserAttributes {
  isKycCompleted?: boolean;
  isPhoneVerified?: boolean;
  isActive?: boolean;
  isPasswordTemporary?: boolean;
  loginAttempts?: number;
  isBlocked?: boolean;
  phone?: string;
  country?: string;
  metadata?: Record<string, unknown>;
}

export interface KeycloakUserResponse {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
  emailVerified: boolean;
  createdTimestamp: number;
  attributes?: UserAttributes;
}

export interface UsersStatsResponse {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  kycPending: number;
  kycApproved: number;
  kycRejected: number;
}

const mapKycStatus = (attrs: UserAttributes | undefined): KycStatus => (attrs?.isKycCompleted ? "approved" : "pending");

const mapAccountStatus = (enabled: boolean, attrs: UserAttributes | undefined): AccountStatus => {
  if (!enabled || attrs?.isBlocked) return "blocked";
  return attrs?.isActive ? "active" : "suspended";
};

export const toUser = (raw: KeycloakUserResponse): User => ({
  id: raw.id,
  email: raw.email ?? raw.username,
  fullName: `${raw.firstName ?? ""} ${raw.lastName ?? ""}`.trim() || raw.username,
  firstName: raw.firstName ?? "",
  lastName: raw.lastName ?? "",
  phone: raw.attributes?.phone || undefined,
  country: raw.attributes?.country ?? "—",
  kycStatus: mapKycStatus(raw.attributes),
  kycTier: "basic",
  accountStatus: mapAccountStatus(raw.enabled, raw.attributes),
  createdAt: raw.createdTimestamp ? new Date(raw.createdTimestamp).toISOString() : "",
  lastLoginAt: undefined,
});

export const toUsersPage = (
  users: KeycloakUserResponse[],
  pagination: ApiPagination
): {users: User[]; pagination: NormalizedPagination} => ({
  users: users.map(toUser),
  pagination: normalizePagination(pagination),
});

export const toUsersStats = (raw: UsersStatsResponse): UsersStatsResponse => raw;
